import React, { useEffect, useRef, useState } from 'react'
import ModalComp from 'components/ModalComp'
import ButtonIcon from 'components/ButtonComp';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import InputComp from 'components/InputComp';
import useForm from 'hooks/useForm';
import Title from 'antd/es/typography/Title';
import { FormState } from 'pages/PMV/Aspects';
import { DEFAULT_PANEL, SUCCESS_PANEL } from 'pages/PMV/DefaultPanels';
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import PreviewComp from 'pages/PMV/Preview';
import usePMVStatuses from 'hooks/usePMVStatuses';
import { IPMVStatus } from 'models/Pmv';
import PMVHooks from 'pages/PMV/PMVHooks';
import logoIts from 'pictures/logo-its-2.png';
import moment from 'moment';

type Props = {
    onClose: () => void;
}

type FormStates = {
    title: string;
    description: string;
    formState: FormState[];
}

const HistoricalPdf: React.FC<Props> = ({ onClose }) => {
    const { processAspects } = PMVHooks();
    const currentDate = new Date();
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        .toISOString()
        .slice(0, 10);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        .toISOString()
        .slice(0, 10);

    const hookForm = useForm<Record<string, any>>({ startDate, endDate });
    const { startDate: formStartDate, endDate: formEndDate } = hookForm.form;
    const hookPMVStatuses = usePMVStatuses({ startDate: formStartDate, endDate: formEndDate });
    const [formState, setFormState] = useState<FormStates[]>([]);
    const previewRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (hookPMVStatuses.data) {
            const newFormState: FormStates[] = hookPMVStatuses.data.map((item: IPMVStatus) => ({
                title: item.pmv.description,
                description: `${moment(item.created_at).format('LLL')} | ${item.is_online ? 'En línea' : 'Desconectado'}` || "",
                formState: processAspects(item.message) || DEFAULT_PANEL,
            }));
            setFormState(newFormState);
        }
    }, [hookPMVStatuses.data]);

    const handleDownload = async () => {
        setIsExporting(true);

        const pdf = new jsPDF();
        let yPosition = 20;

        // Encabezado: Logo y Título
        const PAGE_WIDTH = 210; // A4 en mm
        const LOGO_WIDTH = 30;
        const LOGO_HEIGHT = 15;
        const LOGO_MARGIN = 10;

        pdf.addImage(
            logoIts,
            "PNG",
            LOGO_MARGIN,
            5,
            LOGO_WIDTH,
            LOGO_HEIGHT
        );

        // Título centrado
        pdf.setFontSize(18);
        pdf.text(
            "Reporte Histórico PMV",
            PAGE_WIDTH / 2,
            15,
            { align: "center" }
        );

        yPosition = 30;

        for (let itemIndex = 0; itemIndex < formState.length; itemIndex++) {
            const pmv = formState[itemIndex];
            // Agregar título y descripción
            pdf.setFontSize(14);
            pdf.text(pmv.title, 10, yPosition);
            yPosition += 8;

            if (pmv.description) {
                pdf.setFontSize(11);
                const lines = pdf.splitTextToSize(pmv.description, 180);
                pdf.text(lines, 10, yPosition);
                yPosition += lines.length * 6;
            }

            const IMAGE_HEIGHT = 50;
            const IMAGE_WIDTH = 140;
            const IMAGE_MARGIN = 10;
            const PAGE_HEIGHT = 297; // A4 en mm
            const BOTTOM_MARGIN = 20;

            // Recorrer los aspectos de este grupo
            for (let aspectIndex = 0; aspectIndex < pmv.formState.length; aspectIndex++) {
                const el = previewRefs.current[itemIndex * 10 + aspectIndex];

                if (!el) continue;

                const dataUrl = await toPng(el);
                // Verifica si hay espacio suficiente antes de añadir la imagen
                if (yPosition + IMAGE_HEIGHT + IMAGE_MARGIN > PAGE_HEIGHT - BOTTOM_MARGIN) {
                    pdf.addPage();
                    // Repetir encabezado en cada página nueva
                    pdf.addImage(
                        logoIts,
                        "PNG",
                        LOGO_MARGIN,
                        5,
                        LOGO_WIDTH,
                        LOGO_HEIGHT
                    );
                    pdf.setFontSize(18);
                    pdf.text(
                        "Reporte Histórico PMV",
                        PAGE_WIDTH / 2,
                        15,
                        { align: "center" }
                    );
                    yPosition = 30;
                }

                pdf.addImage(
                    dataUrl,
                    "PNG",
                    (PAGE_WIDTH - IMAGE_WIDTH) / 2, // Centra la imagen
                    yPosition,
                    IMAGE_WIDTH,
                    IMAGE_HEIGHT);
                yPosition += IMAGE_HEIGHT + IMAGE_MARGIN;
            }
            yPosition += 10; // Espacio entre grupos

        }

        pdf.save(`Historical_PMV_${new Date().getTime()}.pdf`);
        setIsExporting(false);
    };

    return (<>
        <ModalComp
            title={<Title level={3}>Reporte Histórico PMV</Title>}
            width={400}
            onClose={onClose}
            footer={[
                <ButtonIcon name={isExporting ? "Generando PDF" : "Descargar"} icon={faFilePdf} variant='outline-success' onClick={handleDownload} disabled={isExporting || hookPMVStatuses.loading} />
            ]}
        >
            <InputComp value={hookForm.form?.startDate} name='startDate' title='Fecha de inicio (opcional)' type='date' onChange={hookForm.handleChange} disabled={isExporting || hookPMVStatuses.loading} /><br />
            <InputComp value={hookForm.form?.endDate} name='endDate' title='Fecha de fin (opcional)' type='date' onChange={hookForm.handleChange} disabled={isExporting || hookPMVStatuses.loading} />
        </ModalComp>
        {/* Contenedor oculto para renderizar los componentes */}
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
            {formState.map((item, itemIndex) =>
                item.formState.map((_, aspectIndex) => (
                    <div
                        key={`${itemIndex}-${aspectIndex}`}
                        ref={(el) => {
                            // Guardar en una posición lineal en previewRefs
                            previewRefs.current[itemIndex * 10 + aspectIndex] = el;
                        }}
                    >
                        <PreviewComp
                            formState={item.formState}
                            currentAspectIndex={aspectIndex}
                        />
                    </div>
                ))
            )}
        </div>
    </>
    )
}

export default HistoricalPdf